/**
 * useBlockers — the React binding for the Blockers board.
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link BlockerStore}
 * (and the existing {@link GoalStore} / {@link WorkstreamStore} / {@link TaskStore}
 * to resolve linked goal / workstream / story titles CLIENT-SIDE for the board
 * cards + the pickers); the stores call the api facade. The page never imports
 * a store or the api directly.
 *
 * The hook also exposes the blocker → goal delivery-impact rollup (via the
 * {@link ./blockerImpact} selector) so the board and the Team Today dashboard
 * can surface forecast impact, and Goal Health / forecast views can consume the
 * same shape.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { TaskStore } from '../tasks/taskStore';
import { BlockerStore } from './blockerStore';
import { sortBlockers } from './blockerModel';
import {
  selectBlockerImpactByGoal,
  selectOverallBlockerImpact,
  type BlockerImpact,
} from './blockerImpact';
import type { Blocker, BlockerDraft, BlockerSummary } from './types';
import type { GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { Task } from '../tasks/types';

export interface UseBlockersResult {
  /** All non-deleted blocker summaries (board-sorted: open first, oldest first). */
  blockers: BlockerSummary[];
  /** Goal summaries (resolve affected-goal titles + goal picker). */
  goals: GoalSummary[];
  /** Workstream summaries (resolve blocked-workstream titles + picker). */
  workstreams: WorkstreamSummary[];
  /** All tasks (resolve blocked-story titles + story picker). */
  tasks: Task[];
  /** Current user's alias (blocked-engineer default on the create form). */
  currentAlias: string;
  /** Map of goalId -> title for board rendering. */
  goalTitles: Map<string, string>;
  /** Map of workstreamId -> title for board rendering. */
  workstreamTitles: Map<string, string>;
  /** Map of storyId (task id) -> title for board rendering. */
  storyTitles: Map<string, string>;
  /** Open-blocker delivery impact keyed by affected goalId. */
  impactByGoal: Map<string, BlockerImpact>;
  /** Overall open-blocker delivery impact across the workspace. */
  overallImpact: BlockerImpact;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (draft: BlockerDraft) => Promise<Blocker>;
  resolve: (id: string) => Promise<void>;
  reopen: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export function useBlockers(): UseBlockersResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      blockers: new BlockerStore(api),
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      tasks: new TaskStore(api),
    }),
    [api],
  );

  const [blockers, setBlockers] = useState<BlockerSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, goalRows, wsRows, allTasks, identity] = await Promise.all([
        stores.blockers.listSummaries(),
        stores.goals.listSummaries(),
        stores.workstreams.listSummaries(),
        stores.tasks.listAll(),
        api.identityProvider.getIdentity(),
      ]);
      setBlockers(sortBlockers(rows));
      setGoals(goalRows);
      setWorkstreams(wsRows);
      setTasks(allTasks);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load blockers.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  const workstreamTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstreams) m.set(w.id, w.title);
    return m;
  }, [workstreams]);

  const storyTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) m.set(t.id, t.title);
    return m;
  }, [tasks]);

  const impactByGoal = useMemo(
    () => selectBlockerImpactByGoal(blockers),
    [blockers],
  );
  const overallImpact = useMemo(
    () => selectOverallBlockerImpact(blockers),
    [blockers],
  );

  const create = useCallback(
    async (draft: BlockerDraft) => {
      const saved = await stores.blockers.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const resolve = useCallback(
    async (id: string) => {
      await stores.blockers.setStatus(id, 'RESOLVED');
      await refresh();
    },
    [stores, refresh],
  );

  const reopen = useCallback(
    async (id: string) => {
      await stores.blockers.setStatus(id, 'OPEN');
      await refresh();
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.blockers.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  return {
    blockers,
    goals,
    workstreams,
    tasks,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    impactByGoal,
    overallImpact,
    loading,
    error,
    refresh,
    create,
    resolve,
    reopen,
    remove,
  };
}
